import React, { HTMLAttributes } from 'react'

import CheckIcon from '@/public/svg/check-circle.svg'
import { Container, Heading } from '@hhgtech/hhg-components'
import { Button, Text } from '@hhgtech/hhg-components/mantine'
import { DataTrackingType, LOCALE } from '@hhgtech/hhg-components/types'
import { ArrowRight } from '@hhgtech/icons/other'
import { useMantineTheme } from '@mantine/core'
import { useCurrentUrl } from 'hooks/useCurrentUrl'
import { useIsMobile } from 'hooks/useIsMobile'
import { useRouterExtend } from 'hooks/useRouterExtend'
import { BabyPoopPage } from 'interfaces/types'
import Link from 'next/link'
import { useIntl } from 'react-intl'

import { colorMap } from '../helpers'
import {
  StyledBabyPoopHeader,
  StyledBabyPoopHeaderContent,
  StyledBabyPoopHeaderContentText,
  StyledBabyPoopHeaderReview,
  StyledBabyPoopListItem,
  StyledBabyPoopSignatureColorBar,
  StyledBabyPoopHeaderContentImage,
} from './index.styled'

type BabyPoopListItemProps = {
  link: string
  item: BabyPoopPage['result'][number]
  wrapperAttributes?: HTMLAttributes<HTMLAnchorElement> & DataTrackingType
}
const BabyPoopListItem = ({
  item,
  link,
  wrapperAttributes,
}: BabyPoopListItemProps) => {
  const { formatMessage: f } = useIntl()
  const theme = useMantineTheme()
  const { image, textInput: title, color } = item

  return (
    <a
      href={link}
      style={{
        textDecoration: 'none',
      }}
      {...wrapperAttributes}
    >
      <StyledBabyPoopListItem
        backgroundColor={colorMap[color].backgroundColor}
        borderColor={colorMap[color].borderColor}
      >
        <img loading="lazy" src={image} alt={title} />
        <StyledBabyPoopSignatureColorBar
          signatureColor={colorMap[color].displayColor || color}
          width={48}
          className="mt-16"
        />
        <Text
          size="s3"
          weight="semiBold"
          className="mt-8"
          color={String(theme.colors.darkGray)}
        >
          {title}
        </Text>
        <Text size="s3" weight="semiBold" className="mt-8" />
        <Text
          size="s5"
          weight="semiBold"
          className="mt-8 item-see-more"
          style={{
            display: 'flex',
            alignItems: 'center',
          }}
        >
          {f({
            id: 'home.seeMore',
            defaultMessage: 'See More',
          })}
          <ArrowRight useCurrentColor={true} size={16} />
        </Text>
        {/* <Text size="s5" weight="semiBold" className="mt-8"></Text>
        <span className="item-see-more">
          {f({
            id: 'home.seeMore',
            defaultMessage: 'See More',
          })}
          <ChevronIcon className="pt-4" />
        </span> */}
      </StyledBabyPoopListItem>
    </a>
  )
}

type BabyPoopHeaderProps = {
  babyPoopPage: BabyPoopPage
  onClickFindOut?: () => void
} & HTMLAttributes<HTMLDivElement>
const BabyPoopHeader = ({
  babyPoopPage,
  onClickFindOut,
  ...rest
}: BabyPoopHeaderProps) => {
  const { formatMessage: f } = useIntl()
  const { locale } = useRouterExtend()
  const isMobile = useIsMobile()

  const currentUrl = useCurrentUrl()

  const { reviewedBy, reviewedDate, isSponsored, sponsorBanner } = babyPoopPage

  const backgroundImage =
    (!isMobile ? sponsorBanner?.desktopImage : sponsorBanner?.mobileImage) ||
    undefined

  return (
    <StyledBabyPoopHeader
      backgroundImage={backgroundImage || ''}
      data-is-vietnam={locale === LOCALE.Vietnam}
      {...rest}
    >
      <Container
        className="px-16"
        style={{
          height: '100%',
        }}
      >
        <StyledBabyPoopHeaderContent
          data-is-vietnam={locale === LOCALE.Vietnam}
        >
          <StyledBabyPoopHeaderContentText isSponsored={isSponsored}>
            <Heading tag="h3" as="h1">
              {babyPoopPage.title}
            </Heading>
            <Text size="p2" className="description">
              {babyPoopPage.description}
            </Text>
            {!!reviewedBy && (
              <StyledBabyPoopHeaderReview className="reviewer">
                <CheckIcon />
                <Text size="p3">
                  {f({
                    id: 'categoryPage.info.reviewed',
                  })}
                  &nbsp;
                  <Link href={reviewedBy.profileUrl} passHref>
                    <a>{reviewedBy.displayName}</a>
                  </Link>
                  &nbsp;
                  {f({ id: 'categoryPage.onDate' }, { date: reviewedDate })}
                </Text>
              </StyledBabyPoopHeaderReview>
            )}
            <Button
              size="lg"
              className="mt-16 find-out-button"
              onClick={onClickFindOut}
              dataEventCategory="Health Tool"
              dataEventAction="Baby Stool Inquiry"
              dataEventLabel={currentUrl}
              style={{
                minWidth: '174px',
                visibility: Boolean(onClickFindOut) ? 'visible' : 'hidden',
              }}
            >
              {f({
                id: 'babyPoop.findOut',
                defaultMessage: 'Find out now',
              })}
            </Button>
          </StyledBabyPoopHeaderContentText>
          {babyPoopPage.featuredImage && (
            <StyledBabyPoopHeaderContentImage
              src={babyPoopPage.featuredImage}
              alt={babyPoopPage.title}
              data-is-vietnam={locale === LOCALE.Vietnam}
            />
          )}
        </StyledBabyPoopHeaderContent>
      </Container>
    </StyledBabyPoopHeader>
  )
}

export { BabyPoopListItem, BabyPoopHeader }
